import { Box, Button, Card, CardContent, CardFooter, Icon, type IconName, Text } from '@careflux/primitives';
import { useState } from 'react';
import { Link } from 'wouter';

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';

import { faqGroups, helpSteps, tutorialVideo } from './content';

export type HelpCenterSection = 'home' | 'quickstart' | 'videos' | 'questions';

const collapsibleContentMotion =
  'overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down motion-reduce:animate-none';

const landingCards: Array<{
  href: string;
  icon: IconName;
  eyebrow: string;
  meta: string;
  title: string;
  description: string;
  preview: 'quickstart' | 'videos' | 'questions';
}> = [
  {
    href: '/faqs/quickstart',
    icon: 'Rocket',
    eyebrow: '18 skref',
    meta: '~7 mín',
    title: 'Quickstart',
    description: 'Skref fyrir skref leiðbeiningar frá innskráningu að fyrstu samantektinni.',
    preview: 'quickstart',
  },
  {
    href: '/faqs/videos',
    icon: 'CirclePlay',
    eyebrow: 'Myndband',
    meta: '11 mín',
    title: 'Kennslumyndbönd',
    description: 'Stutt yfirferð sem sýnir grunnflæði Skríbunnar frá fyrsta viðtali í tilbúna nótu.',
    preview: 'videos',
  },
  {
    href: '/faqs/questions',
    icon: 'BadgeInfo',
    eyebrow: `${faqGroups.flatMap((group) => group.items).length} svör við algengum spurningum`,
    meta: `${faqGroups.length} flokkar`,
    title: 'Algengar spurningar',
    description: 'Persónuvernd, verklag og vandamál - svörin sem heilbrigðisstarfsfólk spyr oftast um.',
    preview: 'questions',
  },
];

export function HelpCenterContent({ section = 'home' }: { section?: HelpCenterSection }) {
  return (
    <Box className="mx-auto flex w-full max-w-6xl flex-col gap-10 px-4 py-8 text-foreground sm:px-6 lg:px-8">
      <HelpCenterHero />

      {section === 'home' && <HelpCenterLanding />}
      {section === 'quickstart' && (
        <>
          <SectionBack
            title="Quickstart"
            subtitle="18 atriði úr quickstart-skjalinu með skjámyndum þar sem þær hjálpa."
          />
          <QuickstartSection />
        </>
      )}
      {section === 'videos' && (
        <>
          <SectionBack
            title="Kennslumyndbönd"
            subtitle="Myndbandið sýnir grunnflæði Skríbunnar frá fyrstu skrefum yfir í tilbúna nótu."
          />
          <VideosSection />
        </>
      )}
      {section === 'questions' && (
        <>
          <SectionBack
            title="Algengar spurningar"
            subtitle="Svörin hér eru skipulögð eftir því sem skiptir mestu máli í daglegri notkun."
          />
          <QuestionsSection />
        </>
      )}

      {section !== 'home' && <ExtraHelpSection />}
    </Box>
  );
}

function HelpCenterHero() {
  return (
    <Box as="section" display="grid" className="gap-4 border-b pb-8">
      <Text asChild size="xs" weight="semibold" color="primary" transform="uppercase" className="w-fit text-[11px]">
        <span>Skríban · Hjálparmiðstöð</span>
      </Text>
      <Box display="grid" className="max-w-3xl gap-3">
        <Text asChild variant="heading" className="text-3xl tracking-normal sm:text-4xl">
          <h1>Komdu þér af stað með Skríbuna</h1>
        </Text>
        <Text asChild color="muted">
          <p>
            Hér finnur þú hnitmiðaða leiðsögn um fyrstu skrefin, stutt kennslumyndband og svör við algengum spurningum
            um notkun, persónuvernd og daglegt verklag.
          </p>
        </Text>
      </Box>
    </Box>
  );
}

function HelpCenterLanding() {
  return (
    <Box as="section" aria-labelledby="help-center-sections" display="grid" className="gap-5">
      <h2 id="help-center-sections" className="sr-only">
        Hjálparflokkar
      </h2>
      <Box data-testid="help-center-landing-grid" display="grid" className="gap-5 lg:auto-rows-fr lg:grid-cols-3">
        {landingCards.map((card) => (
          <Link
            key={card.href}
            href={card.href}
            className="group block h-full rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <Card
              variant="outlined"
              rounded="lg"
              shadow="sm"
              padding="none"
              gap="none"
              className="group-hover:-translate-y-0.5 h-full overflow-hidden transition-all group-hover:border-primary/40 group-hover:shadow-md"
            >
              {card.preview === 'quickstart' && <QuickstartCardPreview />}
              {card.preview === 'videos' && <VideoCardPreview />}
              {card.preview === 'questions' && <QuestionsCardPreview />}

              <CardContent
                data-testid={`landing-card-body-${card.preview}`}
                className="flex flex-1 flex-col gap-5 border-border border-t p-5"
              >
                <Box display="flex" align="center" justify="between" className="gap-3">
                  <Box
                    display="flex"
                    align="center"
                    gap="sm"
                    minWidth="0"
                    className="font-semibold text-[11px] text-primary uppercase"
                  >
                    <Box
                      data-testid={`landing-card-icon-${card.preview}`}
                      display="flex"
                      align="center"
                      justify="center"
                      width="7"
                      height="7"
                      flexShrink="0"
                      rounded="md"
                      className="bg-primary/10"
                    >
                      <Icon name={card.icon} size="xs" />
                    </Box>
                    <Text
                      asChild
                      size="xs"
                      weight="semibold"
                      color="primary"
                      transform="uppercase"
                      className="min-w-0 text-[11px] leading-4"
                    >
                      <span>{card.eyebrow}</span>
                    </Text>
                  </Box>
                  <Text
                    asChild
                    size="xs"
                    color="muted"
                    className="shrink-0 whitespace-nowrap rounded-full bg-muted px-2.5 py-1 text-[11px] leading-none"
                  >
                    <span>{card.meta}</span>
                  </Text>
                </Box>
                <Box display="grid" gap="sm">
                  <Text asChild variant="heading" size="lg" className="tracking-normal">
                    <h3>{card.title}</h3>
                  </Text>
                  <Text asChild size="sm" color="muted" className="leading-6">
                    <p>{card.description}</p>
                  </Text>
                </Box>
                <CardFooter
                  data-testid={`landing-card-footer-${card.preview}`}
                  className="mt-auto justify-between border-border border-t pt-4 font-medium text-primary text-sm"
                >
                  Opna
                  <Box
                    display="flex"
                    align="center"
                    justify="center"
                    width="8"
                    height="8"
                    rounded="full"
                    className="bg-primary/10 transition-transform group-hover:translate-x-0.5"
                  >
                    <Icon name="ArrowRight" size="sm" />
                  </Box>
                </CardFooter>
              </CardContent>
            </Card>
          </Link>
        ))}
      </Box>
    </Box>
  );
}

function QuickstartCardPreview() {
  return (
    <Box
      data-testid="quickstart-card-preview"
      display="flex"
      align="center"
      justify="center"
      className="h-32 overflow-hidden bg-muted/30 px-8"
    >
      <Box
        data-testid="quickstart-card-preview-flow"
        display="flex"
        direction="row"
        align="center"
        justify="between"
        className="before:-translate-y-1/2 relative w-full max-w-56 before:absolute before:top-1/2 before:right-3 before:left-3 before:h-px before:bg-primary/25"
      >
        {[1, 2, 3, 4, 5].map((step) => (
          <Text
            asChild
            key={step}
            size="xs"
            weight="semibold"
            color="inherit"
            className={cn(
              'relative z-10 flex size-7 items-center justify-center rounded-full border',
              step <= 2
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-primary bg-background text-primary'
            )}
          >
            <span>{step <= 2 ? <Icon name="Check" size="xs" /> : step}</span>
          </Text>
        ))}
      </Box>
    </Box>
  );
}

function VideoCardPreview() {
  const bars = [
    { id: 'bar-01', height: 24 },
    { id: 'bar-02', height: 48 },
    { id: 'bar-03', height: 72 },
    { id: 'bar-04', height: 96 },
    { id: 'bar-05', height: 86 },
    { id: 'bar-06', height: 70 },
    { id: 'bar-07', height: 40 },
    { id: 'bar-08', height: 76 },
    { id: 'bar-09', height: 98 },
    { id: 'bar-10', height: 88 },
    { id: 'bar-11', height: 64 },
    { id: 'bar-12', height: 38 },
    { id: 'bar-13', height: 78 },
    { id: 'bar-14', height: 94 },
    { id: 'bar-15', height: 82 },
    { id: 'bar-16', height: 58 },
    { id: 'bar-17', height: 86 },
    { id: 'bar-18', height: 54 },
  ];

  return (
    <Box
      display="flex"
      align="end"
      justify="center"
      gap="xs"
      className="relative h-32 overflow-hidden bg-neutral-900 px-8 pb-5"
    >
      {bars.map((bar) => (
        <span key={bar.id} className="w-2 rounded-sm bg-blue-400" style={{ height: `${bar.height}%` }} />
      ))}
      <span className="absolute inset-0 m-auto flex size-12 items-center justify-center rounded-full bg-background text-foreground shadow-sm">
        <Icon name="Play" size="sm" />
      </span>
    </Box>
  );
}

function QuestionsCardPreview() {
  return (
    <Box data-testid="questions-card-preview" display="grid" className="h-32 gap-1 overflow-hidden bg-muted/30 p-3">
      {['Geymir Skríban hljóðupptökuna?', 'Hvar eru gögnin hýst?', 'Virkar þetta án nettengingar?'].map((question) => (
        <Box key={question} rounded="md" shadow="sm" className="border border-border bg-card px-3 py-1.5">
          <Box
            display="flex"
            align="center"
            justify="between"
            className="gap-3 font-semibold text-[11px] text-foreground"
          >
            <Text asChild size="xs" weight="semibold" className="truncate text-[11px]">
              <span>{question}</span>
            </Text>
            <Icon name="ChevronDown" size="xs" color="muted" />
          </Box>
        </Box>
      ))}
    </Box>
  );
}

function SectionBack({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <Box display="flex" direction="col" className="gap-3 sm:flex-row sm:items-center sm:justify-between">
      <Box display="grid" className="gap-1">
        <Link
          href="/faqs"
          className="flex w-fit items-center gap-2 font-medium text-muted-foreground text-sm transition-colors hover:text-primary"
        >
          <Icon name="ArrowLeft" size="sm" />
          Hjálparmiðstöð
        </Link>
        <Text asChild variant="heading" size="2xl" className="tracking-normal">
          <h2>{title}</h2>
        </Text>
        {subtitle && (
          <Text asChild size="sm" color="muted" className="max-w-3xl leading-6">
            <p>{subtitle}</p>
          </Text>
        )}
      </Box>
    </Box>
  );
}

function QuickstartSection() {
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [completedStepIndexes, setCompletedStepIndexes] = useState<Set<number>>(() => new Set());

  const openStep = (index: number) => {
    setCompletedStepIndexes((current) => {
      const next = new Set(current);
      for (let stepIndex = 0; stepIndex < index; stepIndex += 1) {
        next.add(stepIndex);
      }
      return next;
    });
    setActiveStepIndex(index);
  };

  const completeStep = (index: number) => {
    setCompletedStepIndexes((current) => {
      const next = new Set(current);
      for (let stepIndex = 0; stepIndex <= index; stepIndex += 1) {
        next.add(stepIndex);
      }
      return next;
    });
    setActiveStepIndex(index < helpSteps.length - 1 ? index + 1 : -1);
  };

  return (
    <Box as="section" aria-label="Quickstart" display="grid" className="gap-4">
      <ol className="overflow-hidden rounded-lg border border-border bg-card">
        {helpSteps.map((step, index) => {
          const isActive = activeStepIndex === index;
          const isComplete = completedStepIndexes.has(index);

          return (
            <li
              key={step.title}
              aria-label={`Skref ${index + 1}: ${step.title}`}
              className={cn(
                'border-border border-b transition-colors duration-200 last:border-b-0',
                isActive && 'bg-primary/5'
              )}
            >
              <Collapsible open={isActive} onOpenChange={(open) => (open ? openStep(index) : setActiveStepIndex(-1))}>
                <CollapsibleTrigger className="group grid min-h-12 w-full grid-cols-[1.75rem_minmax(0,1fr)_auto_auto] items-center gap-3 px-4 py-3 text-left outline-none transition-colors hover:bg-muted/50 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
                  <Text
                    asChild
                    size="xs"
                    weight="semibold"
                    color="inherit"
                    data-complete={isComplete}
                    data-testid={`quickstart-step-status-${index + 1}`}
                    className={cn(
                      'flex size-6 shrink-0 items-center justify-center rounded-full transition-colors duration-200',
                      isComplete
                        ? 'bg-primary text-primary-foreground'
                        : isActive
                          ? 'border border-primary bg-background text-primary'
                          : 'bg-muted text-muted-foreground'
                    )}
                  >
                    <span>{isComplete ? <Icon name="Check" size="xs" /> : index + 1}</span>
                  </Text>
                  <Text asChild size="sm" weight="semibold" className="min-w-0 leading-5">
                    <span>{step.title}</span>
                  </Text>
                  <Text
                    asChild
                    size="xs"
                    color="muted"
                    className="hidden rounded-full bg-muted px-2 py-1 text-[11px] leading-none sm:inline-flex"
                  >
                    <span>{step.meta}</span>
                  </Text>
                  <Icon
                    name="ChevronDown"
                    size="sm"
                    className="shrink-0 transition-transform group-data-[state=open]:rotate-180"
                  />
                </CollapsibleTrigger>
                <CollapsibleContent className={collapsibleContentMotion}>
                  <Box display="grid" className="gap-3 px-4 pb-4 sm:pl-16">
                    <Text asChild size="sm" color="muted" className="leading-6">
                      <p>{step.description}</p>
                    </Text>
                    {step.images && (
                      <Box display="grid" className="gap-3">
                        {step.images.map((image) => (
                          <figure
                            key={image.src}
                            className="flex justify-center overflow-hidden rounded-md border border-border bg-muted/20 p-2"
                          >
                            <img
                              src={image.src}
                              alt={image.alt}
                              width={image.width}
                              height={image.height}
                              loading="lazy"
                              className="h-auto max-h-[28rem] w-auto max-w-full rounded border border-border bg-background object-contain"
                            />
                          </figure>
                        ))}
                      </Box>
                    )}
                    <Box display="flex" align="center" justify="between" className="gap-3 pt-1">
                      <Text asChild size="xs" color="muted">
                        <span>
                          {index + 1} af {helpSteps.length}
                        </span>
                      </Text>
                      <Button type="button" size="sm" onClick={() => completeStep(index)}>
                        {index < helpSteps.length - 1 ? 'Næsta skref' : 'Merkja lokið'}
                        <Icon name="ArrowRight" size="sm" />
                      </Button>
                    </Box>
                  </Box>
                </CollapsibleContent>
              </Collapsible>
            </li>
          );
        })}
      </ol>
    </Box>
  );
}

function VideosSection() {
  return (
    <Box as="section" aria-label="Kennslumyndbönd" display="grid" className="gap-4">
      <Box display="grid" className="gap-5 lg:grid-cols-[minmax(0,1fr)_18rem] lg:items-center">
        <Card variant="outlined" rounded="md" shadow="none" padding="none" className="overflow-hidden">
          <Box className="aspect-video bg-muted">
            <iframe
              title={tutorialVideo.title}
              src={tutorialVideo.embedUrl}
              className="h-full w-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          </Box>
        </Card>

        <Box display="flex" direction="col" className="gap-4">
          <Box display="grid" className="gap-2">
            <Text asChild size="xs" weight="semibold" color="primary" transform="uppercase" className="text-[11px]">
              <span>Kennsla</span>
            </Text>
            <Text asChild variant="heading" size="xl" className="tracking-normal">
              <h3>{tutorialVideo.title}</h3>
            </Text>
            <Text asChild size="sm" color="muted" className="leading-6">
              <p>{tutorialVideo.description}</p>
            </Text>
          </Box>
          <Button asChild variant="outline" size="sm" className="w-fit">
            <a href={tutorialVideo.watchUrl} target="_blank" rel="noreferrer">
              Opna á YouTube
              <Icon name="ExternalLink" size="sm" />
            </a>
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

function QuestionsSection() {
  return (
    <Box as="section" aria-label="Algengar spurningar" display="grid" className="gap-5">
      <Box display="grid" className="gap-5">
        {faqGroups.map((group) => {
          const groupHeadingId = `faq-group-${group.id}`;

          return (
            <Box as="section" key={group.title} aria-labelledby={groupHeadingId} display="grid" className="gap-3">
              <Box display="flex" align="center" justify="between" className="gap-3">
                <Box display="flex" align="center" minWidth="0" className="gap-2">
                  <Icon name={group.icon} size="sm" color="muted" />
                  <Text asChild variant="heading" size="sm" className="tracking-normal">
                    <h4 id={groupHeadingId}>{group.title}</h4>
                  </Text>
                </Box>
                <Text asChild size="xs" color="muted">
                  <span>{group.items.length}</span>
                </Text>
              </Box>

              <Card variant="outlined" rounded="lg" shadow="none" padding="none" gap="none" className="overflow-hidden">
                {group.items.map((item, itemIndex) => {
                  const answerTestId = `faq-answer-${item.id}`;

                  return (
                    <Collapsible
                      key={item.question}
                      defaultOpen={item.featured ?? false}
                      className={cn(itemIndex < group.items.length - 1 && 'border-border border-b')}
                    >
                      <CollapsibleTrigger
                        className={cn(
                          'group flex min-h-12 w-full items-center justify-between gap-4 px-4 py-3 text-left font-semibold text-foreground text-sm outline-none transition-colors hover:bg-muted/50 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                          item.featured && 'bg-muted/30'
                        )}
                      >
                        <Text asChild size="sm" weight="semibold" className="min-w-0 break-words">
                          <span>{item.question}</span>
                        </Text>
                        <Icon
                          name="ChevronDown"
                          size="sm"
                          className="shrink-0 transition-transform group-data-[state=open]:rotate-180"
                        />
                      </CollapsibleTrigger>
                      <CollapsibleContent className={collapsibleContentMotion}>
                        <Box data-testid={answerTestId} className="px-4 pr-12 pb-4">
                          <div className="text-muted-foreground text-sm leading-6">{item.answer}</div>
                        </Box>
                      </CollapsibleContent>
                    </Collapsible>
                  );
                })}
              </Card>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

function ExtraHelpSection() {
  return (
    <Box as="section" aria-label="Vantar þig meiri hjálp?" className="border-t pt-6">
      <Card variant="outlined" rounded="lg" shadow="none" padding="md">
        <CardContent className="p-0">
          <Text asChild variant="heading" size="md" className="tracking-normal">
            <h2>Vantar þig meiri hjálp?</h2>
          </Text>
          <Text asChild size="sm" color="muted" className="mt-1 leading-6">
            <p>
              Notaðu hjálpar- eða endurgjafarhnappinn í Skríbunni ef þú finnur ekki svarið hér eða þarft að senda nánari
              lýsingu á vandamáli.
            </p>
          </Text>
        </CardContent>
      </Card>
    </Box>
  );
}
